import React, { useEffect, useState } from "react"
import axios from "axios"
import { FileBarChart, ThumbsUp, ThumbsDown, Lightbulb, AlertTriangle } from "lucide-react"
import { ReportCard, ReportSectionHeader, ReportStatTile, ReportEmptyState, ReportSkeleton } from "../components/ReportUI"
import { REPORT_COLORS } from "../components/reportTheme"
import { ai_api } from "../../../utils/variables"
import { buildMetricCards } from "../../../utils/aiReportMetrics"

// Lightweight version of the parser in additional_components/common/AIReportDisplay.jsx —
// pulls only what the condensed summary needs (metrics, sentiment split, top observations/recommendations).
const parseReport = data => {
  const parsed = { metrics: [], sentiment: null, observations: [], recommendations: [] }
  if (!data || typeof data !== "object") return parsed

  parsed.metrics = buildMetricCards(data.metrics)

  if (data.sentiment?.breakdown) {
    parsed.sentiment = {
      positive: data.sentiment.breakdown.positive_percent ?? 0,
      neutral: data.sentiment.breakdown.neutral_percent ?? 0,
      negative: data.sentiment.breakdown.negative_percent ?? 0,
    }
  }

  parsed.observations = (data.key_observations || []).map(o => ({ title: o.title || "", explanation: o.explanation || "" }))

  const rec = data.recommendations || {}
  parsed.recommendations = [
    ...(rec.bug_fix_plan || []),
    ...(rec.keyword_tracking_monitoring || []),
    ...(rec.keyword_expansion || []).map(k => `${k.keyword} (${k.label})`),
  ]

  return parsed
}

const formatDateYYYYMMDD = date => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export default function AIReportSummaryTab({ appId, appName }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [parsed, setParsed] = useState(null)

  useEffect(() => {
    if (!appId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    const token = localStorage.getItem("authToken")
    const headers = { "Content-Type": "application/json" }
    if (token) headers.Authorization = `Bearer ${token}`

    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 30)

    axios
      .request({
        method: "post",
        url: `${ai_api}/chat/app-info/`,
        headers,
        data: JSON.stringify({
          app_id: `${appId}`,
          force_refresh: "false",
          start_date: formatDateYYYYMMDD(startDate),
          end_date: formatDateYYYYMMDD(endDate),
        }),
      })
      .then(({ data, status }) => {
        if (cancelled) return
        if (status === 200) setParsed(parseReport(data.response))
        else setError("Failed to load report")
      })
      .catch(err => !cancelled && setError(err.response?.data?.message || err.response?.data?.error || err.message || "Failed to load report"))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [appId])

  if (!appId) return null
  if (loading) return <ReportSkeleton tiles={4} />
  if (error) return <ReportEmptyState icon={AlertTriangle} title="Failed to Load Report" description={error} />
  if (!parsed) return null

  const { positive = 0, neutral = 0, negative = 0 } = parsed.sentiment || {}
  const hasSentiment = positive + neutral + negative > 0

  return (
    <div>
      <ReportSectionHeader icon={FileBarChart} eyebrow="AI Reports" title={appName ? `${appName} — Performance Report` : "Performance Report"} />

      {parsed.metrics.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
          {parsed.metrics.slice(0, 4).map((m, i) => (
            <ReportStatTile key={i} label={m.label} value={m.value} delta={m.direction !== "neutral" ? m.change : null} deltaDirection={m.direction === "neutral" ? "flat" : m.direction} />
          ))}
        </div>
      )}

      {hasSentiment && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>User Sentiment</div>
          <div style={{ display: "flex", height: 10, borderRadius: 6, overflow: "hidden", gap: 2, marginBottom: 10 }}>
            {positive > 0 && <div style={{ flexBasis: `${positive}%`, backgroundColor: REPORT_COLORS.good }} />}
            {neutral > 0 && <div style={{ flexBasis: `${neutral}%`, backgroundColor: REPORT_COLORS.warning }} />}
            {negative > 0 && <div style={{ flexBasis: `${negative}%`, backgroundColor: REPORT_COLORS.critical }} />}
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
            <span style={{ color: REPORT_COLORS.textSecondary }}><ThumbsUp size={12} color={REPORT_COLORS.good} style={{ marginRight: 4, verticalAlign: -1 }} />Positive {positive}%</span>
            <span style={{ color: REPORT_COLORS.textSecondary }}>Neutral {neutral}%</span>
            <span style={{ color: REPORT_COLORS.textSecondary }}><ThumbsDown size={12} color={REPORT_COLORS.critical} style={{ marginRight: 4, verticalAlign: -1 }} />Negative {negative}%</span>
          </div>
        </ReportCard>
      )}

      {(parsed.observations.length > 0 || parsed.recommendations.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
          {parsed.observations.length > 0 && (
            <ReportCard>
              <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>Key Observations</div>
              {parsed.observations.slice(0, 3).map((o, i) => (
                <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < 2 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                  <strong style={{ color: REPORT_COLORS.textPrimary }}>{o.title}</strong>
                  {o.explanation && <div style={{ marginTop: 2 }}>{o.explanation.slice(0, 100)}{o.explanation.length > 100 ? "…" : ""}</div>}
                </div>
              ))}
            </ReportCard>
          )}
          {parsed.recommendations.length > 0 && (
            <ReportCard>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Lightbulb size={14} color={REPORT_COLORS.warning} />
                <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Top Recommendations</span>
              </div>
              {parsed.recommendations.slice(0, 4).map((r, i) => (
                <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 6, display: "flex", gap: 8 }}>
                  <span style={{ color: REPORT_COLORS.accent, fontWeight: 700 }}>•</span>
                  {r}
                </div>
              ))}
            </ReportCard>
          )}
        </div>
      )}
    </div>
  )
}
